import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import { nanoid } from "nanoid";
import {
  COL_CHECKLISTS,
  COL_DOCS,
  COL_RECOMMENDATIONS,
  MAX_CLOSING_EXPANSION,
  MAX_ITEMS_PER_CHECKLIST,
  governingRole,
  type Checklist,
  type ChecklistItem,
  type ClosingDoc,
  type ItemComment,
  type Recommendation,
  type RecommendationRun,
  type Section,
  type TemplateItem,
} from "@closing/shared";
import { auth, db } from "./firebase";

export function checklistRef(dealId: string) {
  return doc(db, COL_CHECKLISTS, dealId);
}

export function watchChecklist(
  dealId: string,
  cb: (checklist: Checklist | null) => void,
): Unsubscribe {
  return onSnapshot(checklistRef(dealId), (snap) => {
    cb(snap.exists() ? (snap.data() as Checklist) : null);
  });
}

function me(): { email: string; name: string } {
  const u = auth.currentUser;
  return { email: u?.email?.toLowerCase() ?? "", name: u?.displayName ?? u?.email ?? "" };
}

/**
 * Patches fields of one checklist item via dot paths — the CRM's pattern:
 * cheap, concurrent-safe per field, and every open client re-renders from
 * the snapshot listener.
 */
async function patchItem(
  dealId: string,
  itemId: string,
  fields: Record<string, unknown>,
): Promise<void> {
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const [k, v] of Object.entries(fields)) {
    patch[`items.${itemId}.${k}`] = v;
  }
  await updateDoc(checklistRef(dealId), patch);
}

export async function setItemCompleted(
  dealId: string,
  itemId: string,
  completed: boolean,
): Promise<void> {
  await patchItem(dealId, itemId, {
    completed,
    completedAt: completed ? serverTimestamp() : null,
    completedBy: completed ? me().email : null,
  });
}

export async function setItemAssignee(
  dealId: string,
  itemId: string,
  assignee: { email: string; displayName: string } | null,
): Promise<void> {
  await patchItem(dealId, itemId, {
    assigneeEmail: assignee?.email ?? null,
    assigneeName: assignee?.displayName ?? null,
  });
}

/**
 * Sets the same assignee on many items — the "reassign every research item"
 * do-over. Runs as a transaction that re-reads the checklist: an id whose
 * item was deleted while the panel sat open is skipped (a blind dot-path
 * update would resurrect it as a two-field ghost), and with `skipCompleted`
 * an item someone finished in the meantime is left alone too. Only the ids
 * passed are candidates; the caller decides which items qualify by role.
 * Returns how many items were actually changed.
 */
export async function reassignItems(
  dealId: string,
  itemIds: string[],
  assignee: { email: string; displayName: string } | null,
  opts: { skipCompleted?: boolean } = {},
): Promise<number> {
  if (itemIds.length === 0) return 0;
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(checklistRef(dealId));
    if (!snap.exists()) return 0;
    const items = (snap.data() as Checklist).items;
    const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
    let n = 0;
    for (const id of itemIds) {
      const item = items[id];
      if (!item) continue;
      if (opts.skipCompleted && item.completed) continue;
      patch[`items.${id}.assigneeEmail`] = assignee?.email ?? null;
      patch[`items.${id}.assigneeName`] = assignee?.displayName ?? null;
      n++;
    }
    if (n > 0) tx.update(checklistRef(dealId), patch);
    return n;
  });
}

export async function setItemDueOn(
  dealId: string,
  itemId: string,
  dueOn: string | null,
): Promise<void> {
  await patchItem(dealId, itemId, { dueOn });
}

export async function renameItem(dealId: string, itemId: string, name: string): Promise<void> {
  await patchItem(dealId, itemId, { name });
}

/** Sets/clears the Yes/No answer (item must have hasYesNo). */
export async function setItemAnswer(
  dealId: string,
  itemId: string,
  answer: "yes" | "no" | null,
): Promise<void> {
  await patchItem(dealId, itemId, { answer });
}

/** Enables/disables the Yes/No chip on an item; disabling clears the answer. */
export async function setItemYesNo(
  dealId: string,
  itemId: string,
  hasYesNo: boolean,
): Promise<void> {
  await patchItem(dealId, itemId, { hasYesNo, ...(hasYesNo ? {} : { answer: null }) });
}

/**
 * Sets an item's gate (per-deal). Transactional: the cycle check runs
 * against the CURRENT items graph, so a concurrent gate edit from another
 * client can't combine with this one into a loop of forever-locked items.
 */
export async function setItemGate(
  dealId: string,
  itemId: string,
  gateItemId: string | null,
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(checklistRef(dealId));
    if (!snap.exists()) return;
    const items = (snap.data() as Checklist).items;
    if (!items[itemId]) return;
    if (gateItemId) {
      if (!items[gateItemId]) throw new Error("That gate item no longer exists.");
      // Walk dependsOn edges from the candidate gate: reaching this item
      // means the gate (transitively) depends on it — a cycle.
      const reaches = (fromId: string, seen = new Set<string>()): boolean => {
        if (fromId === itemId) return true;
        if (seen.has(fromId)) return false;
        seen.add(fromId);
        return (items[fromId]?.dependsOn ?? []).some((d) => reaches(d, seen));
      };
      if (reaches(gateItemId)) {
        throw new Error("That would create a circular gate — pick a different item.");
      }
    }
    tx.update(checklistRef(dealId), {
      [`items.${itemId}.dependsOn`]: gateItemId ? [gateItemId] : [],
      updatedAt: serverTimestamp(),
    });
  });
}

export async function setItemDescription(
  dealId: string,
  itemId: string,
  description: string | null,
): Promise<void> {
  await patchItem(dealId, itemId, { description });
}

export async function addItem(
  dealId: string,
  checklist: Checklist,
  sectionId: string,
  name: string,
): Promise<string> {
  if (Object.keys(checklist.items).length >= MAX_ITEMS_PER_CHECKLIST) {
    throw new Error(`Checklist is at the ${MAX_ITEMS_PER_CHECKLIST}-item cap.`);
  }
  const id = nanoid(12);
  const inSection = Object.values(checklist.items).filter((i) => i.sectionId === sectionId);
  const sortOrder = Math.max(0, ...inSection.map((i) => i.sortOrder)) + 1;
  const item: ChecklistItem = {
    id,
    name,
    description: null,
    sectionId,
    assigneeEmail: null,
    assigneeName: null,
    completed: false,
    completedAt: null,
    completedBy: null,
    dueOn: null,
    sortOrder,
    source: "manual",
    sourceRef: null,
    commentCount: 0,
    createdAt: serverTimestamp(),
    createdBy: me().email,
  };
  await updateDoc(checklistRef(dealId), {
    [`items.${id}`]: item,
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function deleteItem(dealId: string, itemId: string): Promise<void> {
  await updateDoc(checklistRef(dealId), {
    [`items.${itemId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** Swaps sortOrder with the neighbor above/below within the same section. */
export async function moveItem(
  dealId: string,
  checklist: Checklist,
  itemId: string,
  direction: "up" | "down",
): Promise<void> {
  const item = checklist.items[itemId];
  if (!item) return;
  const siblings = Object.values(checklist.items)
    .filter((i) => i.sectionId === item.sectionId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const idx = siblings.findIndex((i) => i.id === itemId);
  const neighbor = siblings[direction === "up" ? idx - 1 : idx + 1];
  if (!neighbor) return;
  await updateDoc(checklistRef(dealId), {
    [`items.${itemId}.sortOrder`]: neighbor.sortOrder,
    [`items.${neighbor.id}.sortOrder`]: item.sortOrder,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Drag-and-drop move: places an item in `targetSectionId`, inserted before
 * `beforeItemId` (or at the end when null). Renumbers the target section's
 * sortOrders in one update so ordering is deterministic.
 */
export async function moveItemTo(
  dealId: string,
  checklist: Checklist,
  itemId: string,
  targetSectionId: string,
  beforeItemId: string | null,
): Promise<void> {
  const dragged = checklist.items[itemId];
  if (!dragged || itemId === beforeItemId) return;

  const targetItems = Object.values(checklist.items)
    .filter((i) => i.sectionId === targetSectionId && i.id !== itemId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const insertAt = beforeItemId
    ? Math.max(0, targetItems.findIndex((i) => i.id === beforeItemId))
    : targetItems.length;
  targetItems.splice(insertAt, 0, dragged);

  const patch: Record<string, unknown> = {
    [`items.${itemId}.sectionId`]: targetSectionId,
    updatedAt: serverTimestamp(),
  };
  targetItems.forEach((i, idx) => {
    patch[`items.${i.id}.sortOrder`] = idx + 1;
  });
  await updateDoc(checklistRef(dealId), patch);
}

export async function addSection(
  dealId: string,
  checklist: Checklist,
  name: string,
): Promise<void> {
  const section: Section = {
    id: nanoid(8),
    name,
    sortOrder: Math.max(0, ...checklist.sections.map((s) => s.sortOrder)) + 1,
  };
  await updateDoc(checklistRef(dealId), {
    sections: arrayUnion(section),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Rewrites the sections array via a transform of the CURRENT document state.
 * Sections is an array field, so any edit is a full-array rewrite — doing
 * that from a possibly-stale snapshot would silently drop sections another
 * client just added/renamed. The transaction re-reads before writing.
 */
async function transformSections(
  dealId: string,
  transform: (sections: Section[]) => Section[] | null,
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(checklistRef(dealId));
    if (!snap.exists()) return;
    const current = (snap.data() as Checklist).sections;
    const next = transform(current);
    if (next === null) return; // no-op (e.g. target vanished, already at edge)
    tx.update(checklistRef(dealId), { sections: next, updatedAt: serverTimestamp() });
  });
}

export async function renameSection(
  dealId: string,
  _checklist: Checklist,
  sectionId: string,
  name: string,
): Promise<void> {
  await transformSections(dealId, (sections) =>
    sections.some((s) => s.id === sectionId)
      ? sections.map((s) => (s.id === sectionId ? { ...s, name } : s))
      : null,
  );
}

/**
 * Swaps a section's sortOrder with its nearest visible neighbor above/below.
 * Deactivated sections are skipped: reordering happens among active sections
 * only (they're the only ones with reorder arrows), so a hidden section
 * sitting between two visible ones doesn't absorb the swap and stall the move.
 */
export async function moveSection(
  dealId: string,
  _checklist: Checklist,
  sectionId: string,
  direction: "up" | "down",
): Promise<void> {
  await transformSections(dealId, (sections) => {
    const ordered = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = ordered.findIndex((s) => s.id === sectionId);
    if (idx < 0) return null;
    const step = direction === "up" ? -1 : 1;
    let neighborIdx = idx + step;
    while (
      neighborIdx >= 0 &&
      neighborIdx < ordered.length &&
      ordered[neighborIdx]!.active === false
    ) {
      neighborIdx += step;
    }
    if (neighborIdx < 0 || neighborIdx >= ordered.length) return null;
    const a = ordered[idx]!;
    const b = ordered[neighborIdx]!;
    return sections.map((s) =>
      s.id === a.id
        ? { ...s, sortOrder: b.sortOrder }
        : s.id === b.id
          ? { ...s, sortOrder: a.sortOrder }
          : s,
    );
  });
}

/**
 * Removes an empty section. Runs in a transaction so the emptiness check is
 * against the CURRENT doc — a concurrent "add item" from another client
 * aborts the delete instead of orphaning the new item.
 */
export async function deleteSection(
  dealId: string,
  sectionId: string,
  opts: { withItems?: boolean; confirmedItemIds?: string[] } = {},
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(checklistRef(dealId));
    if (!snap.exists()) return;
    const data = snap.data() as Checklist;
    const sectionItems = Object.values(data.items).filter((i) => i.sectionId === sectionId);
    if (sectionItems.length > 0 && !opts.withItems) {
      throw new Error("Move or delete this section's items first.");
    }
    // The user confirmed deleting a SPECIFIC set of items. If the section
    // gained a member between confirm and commit (concurrent add/move, or a
    // transaction retry), abort rather than silently deleting something the
    // user never saw. Items that vanished meanwhile are fine — deleting
    // fewer than confirmed is safe.
    const confirmed = new Set(opts.confirmedItemIds ?? []);
    const unconfirmed = sectionItems.filter((i) => !confirmed.has(i.id));
    if (opts.withItems && unconfirmed.length > 0) {
      throw new Error(
        `This section changed while you were confirming (${unconfirmed.length} new item(s)) — review it and try again.`,
      );
    }
    if (!data.sections.some((s) => s.id === sectionId)) return;

    const patch: Record<string, unknown> = {
      sections: data.sections.filter((s) => s.id !== sectionId),
      updatedAt: serverTimestamp(),
    };
    const removedIds = new Set(sectionItems.map((i) => i.id));
    for (const id of removedIds) patch[`items.${id}`] = deleteField();
    // Surviving items must not gate on deleted ones.
    for (const item of Object.values(data.items)) {
      if (removedIds.has(item.id)) continue;
      const deps = item.dependsOn ?? [];
      const kept = deps.filter((d) => !removedIds.has(d));
      if (kept.length !== deps.length) patch[`items.${item.id}.dependsOn`] = kept;
    }
    tx.update(checklistRef(dealId), patch);
  });
}

/**
 * Deactivates (hides, preserving) or reactivates a section. Deactivation is
 * the non-destructive alternative to {@link deleteSection}: the section keeps
 * its items in place — only its `active` flag flips — so a later reactivate
 * restores it exactly, completion state and all. Written through
 * transformSections so a concurrent rename/reorder isn't clobbered.
 */
export async function setSectionActive(
  dealId: string,
  sectionId: string,
  active: boolean,
): Promise<void> {
  await transformSections(dealId, (sections) =>
    sections.some((s) => s.id === sectionId)
      ? sections.map((s) => (s.id === sectionId ? { ...s, active } : s))
      : null,
  );
}

/**
 * Adds a new section to a live checklist pre-filled from a template section:
 * copies the section heading plus every template item under it (fresh, so no
 * completion/assignee state carries over). Item ids are regenerated, so
 * intra-section gates (`dependsOn`) are remapped to the new ids and any gate
 * pointing outside the copied set is dropped — mirroring instantiation.
 */
export async function addSectionFromTemplate(
  dealId: string,
  checklist: Checklist,
  templateSectionName: string,
  templateItems: TemplateItem[],
): Promise<void> {
  if (Object.keys(checklist.items).length + templateItems.length > MAX_ITEMS_PER_CHECKLIST) {
    throw new Error(
      `Adding this section (${templateItems.length} item(s)) would exceed the ${MAX_ITEMS_PER_CHECKLIST}-item checklist cap.`,
    );
  }
  const section: Section = {
    id: nanoid(8),
    name: templateSectionName,
    sortOrder: Math.max(0, ...checklist.sections.map((s) => s.sortOrder)) + 1,
  };
  const ordered = [...templateItems].sort((a, b) => a.sortOrder - b.sortOrder);
  const idMap = new Map(ordered.map((t) => [t.id, nanoid(12)] as const));

  const patch: Record<string, unknown> = {
    sections: arrayUnion(section),
    updatedAt: serverTimestamp(),
  };
  ordered.forEach((t, idx) => {
    const id = idMap.get(t.id)!;
    const item: ChecklistItem = {
      id,
      name: t.name,
      description: t.description ?? null,
      sectionId: section.id,
      assigneeEmail: null,
      assigneeName: null,
      completed: false,
      completedAt: null,
      completedBy: null,
      dueOn: null,
      sortOrder: idx + 1,
      source: "template",
      sourceRef: t.id,
      dependsOn: (t.dependsOn ?? [])
        .filter((d) => idMap.has(d))
        .map((d) => idMap.get(d)!),
      hasYesNo: t.hasYesNo ?? false,
      answer: null,
      autoAssignRole: governingRole(t),
      commentCount: 0,
      createdAt: serverTimestamp(),
      createdBy: me().email,
    };
    patch[`items.${id}`] = item;
  });
  await updateDoc(checklistRef(dealId), patch);
}

// ---------------------------------------------------------------------------
// Comments (closing-checklists/{dealId}/comments subcollection)
// ---------------------------------------------------------------------------

export function watchItemComments(
  dealId: string,
  itemId: string,
  cb: (comments: (ItemComment & { id: string })[]) => void,
): Unsubscribe {
  const q = query(
    collection(checklistRef(dealId), "comments"),
    where("itemId", "==", itemId),
    orderBy("createdAt", "asc"),
  );
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ItemComment) })));
  });
}

export async function addComment(
  dealId: string,
  itemId: string,
  text: string,
  mentions: string[],
): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("not_signed_in");
  const comment: ItemComment = {
    itemId,
    text,
    authorUid: u.uid,
    authorEmail: u.email?.toLowerCase() ?? "",
    authorName: u.displayName ?? u.email ?? "",
    mentions,
    createdAt: serverTimestamp(),
  };
  await addDoc(collection(checklistRef(dealId), "comments"), comment);
  await updateDoc(checklistRef(dealId), {
    [`items.${itemId}.commentCount`]: increment(1),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteComment(
  dealId: string,
  itemId: string,
  commentId: string,
): Promise<void> {
  await deleteDoc(doc(checklistRef(dealId), "comments", commentId));
  await updateDoc(checklistRef(dealId), {
    [`items.${itemId}.commentCount`]: increment(-1),
    updatedAt: serverTimestamp(),
  });
}

// ---------------------------------------------------------------------------
// Documents + AI recommendation runs
// ---------------------------------------------------------------------------

export function watchDealDocs(
  dealId: string,
  cb: (docs: (ClosingDoc & { id: string })[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, COL_DOCS),
    where("dealId", "==", dealId),
    orderBy("uploadedAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    cb(
      snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as ClosingDoc) }))
        .filter((d) => d.status === "uploaded"),
    );
  });
}

export function watchLatestRun(
  dealId: string,
  cb: (run: (RecommendationRun & { id: string }) | null) => void,
): Unsubscribe {
  const q = query(
    collection(db, COL_RECOMMENDATIONS),
    where("dealId", "==", dealId),
    orderBy("createdAt", "desc"),
    limit(1),
  );
  return onSnapshot(q, (snap) => {
    const d = snap.docs[0];
    cb(d ? { id: d.id, ...(d.data() as RecommendationRun) } : null);
  });
}

/**
 * Accepts a recommendation: creates the checklist item(s) — one per closing
 * for per-closing recs — then records the decision on the run doc (the only
 * run field clients may write).
 */
export async function acceptRecommendation(
  dealId: string,
  checklist: Checklist,
  runId: string,
  rec: Recommendation,
  opts: { sectionId: string; closingCount: number | null },
): Promise<void> {
  const copies =
    rec.perClosing && opts.closingCount && opts.closingCount > 1
      ? Math.min(opts.closingCount, MAX_CLOSING_EXPANSION)
      : 1;

  // Same doc-size budget the manual addItem path enforces — the whole
  // checklist lives in one Firestore document.
  if (Object.keys(checklist.items).length + copies > MAX_ITEMS_PER_CHECKLIST) {
    throw new Error(
      `Accepting this would exceed the ${MAX_ITEMS_PER_CHECKLIST}-item checklist cap.`,
    );
  }

  const inSection = Object.values(checklist.items).filter(
    (i) => i.sectionId === opts.sectionId,
  );
  let sortOrder = Math.max(0, ...inSection.map((i) => i.sortOrder));
  const me = auth.currentUser;
  const createdBy = me?.email?.toLowerCase() ?? "";

  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
  const itemIds: string[] = [];
  for (let n = 1; n <= copies; n++) {
    const id = nanoid(12);
    itemIds.push(id);
    const item: ChecklistItem = {
      id,
      name: copies > 1 ? `${rec.title} — Closing ${n}` : rec.title,
      description: rec.description || null,
      sectionId: opts.sectionId,
      assigneeEmail: null,
      assigneeName: null,
      completed: false,
      completedAt: null,
      completedBy: null,
      dueOn: null,
      sortOrder: ++sortOrder,
      source: "ai",
      sourceRef: `${runId}:${rec.id}`,
      commentCount: 0,
      createdAt: serverTimestamp(),
      createdBy,
    };
    patch[`items.${id}`] = item;
  }
  await updateDoc(checklistRef(dealId), patch);

  await updateDoc(doc(db, COL_RECOMMENDATIONS, runId), {
    [`decisions.${rec.id}`]: {
      status: "accepted",
      acceptedItemIds: itemIds,
      decidedBy: createdBy,
      decidedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

export async function dismissRecommendation(runId: string, recId: string): Promise<void> {
  await updateDoc(doc(db, COL_RECOMMENDATIONS, runId), {
    [`decisions.${recId}`]: {
      status: "dismissed",
      acceptedItemIds: null,
      decidedBy: auth.currentUser?.email?.toLowerCase() ?? "",
      decidedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

/** Creates a new section and returns its id (for accept-into-new-section). */
export async function addSectionReturningId(
  dealId: string,
  checklist: Checklist,
  name: string,
): Promise<string> {
  const section: Section = {
    id: nanoid(8),
    name,
    sortOrder: Math.max(0, ...checklist.sections.map((s) => s.sortOrder)) + 1,
  };
  await updateDoc(checklistRef(dealId), {
    sections: arrayUnion(section),
    updatedAt: serverTimestamp(),
  });
  return section.id;
}

/** 'YYYY-MM-DD' for today in the user's local timezone. */
export function todayYMD(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function watchCollection<T>(
  name: string,
  cb: (rows: (T & { id: string })[]) => void,
): Unsubscribe {
  return onSnapshot(collection(db, name), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) })));
  });
}
